import { useCallback, useEffect, useState } from "react";

import { ChevronDown, Cog, ExternalLink } from "lucide-react";
import { Select, Row, Col, Label, FormGroup, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, Button } from 'twyne-new.ui'

import Params from "./Params";
import Previewer from "./Previewer";
import URLGroup from "./URLGroup";
import { getURL, getData, parseParams, useSite, useRoutes } from "./utils";

export default function PreviewTab({ data: page, show, extraFields }) {
  const { domains, defaultDomain, hasCms } = useSite()

  const [data, setData] = useState(null)

  const [protocol, setProtocol] = useState("")
  const [domain, setDomain] = useState("")
  const [route, setRoute] = useState("flow")
  const [pageId, setPageId] = useState(null)
  const [params, setParams] = useState({})
  const [url, setUrl] = useState("")

  useEffect(() => {
    if (!protocol) {
      const protocol = window.location.protocol.includes("https") ? "https" : "http";
      setProtocol(protocol)
    }
  }, [protocol])

  useEffect(() => {
    if (defaultDomain && !domain) {
      setDomain(defaultDomain)
    }
  }, [defaultDomain, domain])

  useEffect(() => {
    if (page && !pageId) {
      setPageId(page.id)
    }
  }, [page, pageId])

  useEffect(() => {
    if (!data) {
      const initial = getData()
      setData(initial)
      setParams(parseParams(initial))
    }
  }, [data])

  useEffect(() => {
    if (domain && pageId) {
      setUrl(getURL({
        hasCms,
        protocol,
        domain,
        route,
        pageId,
        params,
      }))
    }
  }, [hasCms, protocol, domain, route, pageId, params])

  const handleDomain = useCallback(option => {
    const { value } = option
    setDomain(value)
  }, [])

  const handleRoute = useCallback(option => {
    const { value } = option
    setRoute(value)
  }, [])

  const handleOpen = useCallback(() => {
    window.open(url, "_blank")
  }, [url])

  const handleReload = useCallback(() => {
    setUrl("")
    setParams({ ...params })
  }, [params])

  const handleApply = useCallback(data => {
    setParams(parseParams(data))
  }, [])

  return <>
    <Row className="items-end">
      <Group size="2" hasCms={hasCms}>
        <Domain domains={domains} value={domain} onChange={handleDomain} />
      </Group>
      <Group size="2" hasCms={hasCms}>
        <Route value={route} onChange={handleRoute} />
      </Group>
      <Group size="5" hasCms={hasCms} className=''>
        <URLGroup url={url} protocol={protocol} onProtocol={setProtocol} />
      </Group>
      <Col size="3" className='flex-row!'>
        <Button color="accent" onClick={handleOpen}>
          <ExternalLink size={16} />
          Open
        </Button>
        <Options hasCms={hasCms} data={data} extraFields={extraFields}
          onApply={handleApply} onReload={handleReload} />
      </Col>
      <Group size="12">
        <Previewer show={true} pageId={pageId} url={url} params={params} />
      </Group>
    </Row>
  </>
}

function Group({ size, hasCms = true, children, className = '' }) {
  if (!hasCms) {
    return null
  }
  return <Col size={size}>
    <FormGroup className={className}>
      {children}
    </FormGroup>
  </Col>
}

function Domain({ domains, value, onChange }) {
  return <>
    <Label>Domain</Label>
    <Select options={domains} value={value} onChange={onChange} clearable={false} />
  </>
}

function Route({ value, onChange }) {
  const { routes } = useRoutes()

  return <>
    <Label>Route</Label>
    <Select options={routes} value={value} onChange={onChange} clearable={false} />
  </>
}

function Options({ data, extraFields, onApply, onReload }) {
  const [isOpen, setIsOpen] = useState(false)

  const handleToggle = useCallback(() => {
    setIsOpen(open => !open)
  }, [])

  return <>
    <DropdownMenu size='sm'>
      <DropdownMenuTrigger asChild>
        <Button color='accent' variant='outline' className='w-fit h-full'>
          <Cog size={16} />
          Options
          <ChevronDown size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => setIsOpen(true)}>
          Configure Params
        </DropdownMenuItem>
        <DropdownMenuItem key="reload" onClick={onReload}>
          Reload Preview
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <Params extraFields={extraFields} isOpen={isOpen}
        toggle={handleToggle} value={data} onApply={onApply} />
  </>
}
